<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type { Item } from '@/app/components/SearchDropdown/SearchDropdown.vue';
import {
  getFormattedDate,
  getFormattedTimezoneDetail,
} from '@/campaign/utils/utils';
import {
  roundToNearestHours,
  eachHourOfInterval,
  eachMinuteOfInterval,
} from 'date-fns';
import { DATA_QA } from './time-select.constants';
import logger from '@/logger';
import type { SearchDropdownError } from '@/app/components/SearchDropdown/search-dropdown.constants';

const log = logger.getChild('TimeSelect');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

type Props = {
  startOn: Date;
  endOn: Date;
  label?: string;
  placeholder?: string;
  required?: boolean;
  minTime?: Date | null;
  maxTime?: Date | null;
  errorMessages?: Record<string, string>;
  showTimezone?: boolean;
  disabled?: boolean;
  step?: number;
  itemTitleFormatFunction?: (date: Date) => string;
  hideIcon?: boolean;
};

const props = defineProps<Props>();

const model = defineModel<Date | null>();

const emit = defineEmits<{
  (e: 'update:opened', isOpen: boolean): void;
  (e: 'update:error', error: SearchDropdownError | null): void;
}>();

const modelValue = ref<Item<Date> | null>(null);
const filter = ref('');

const startTime = computed(() =>
  roundToNearestHours(props.startOn, { roundingMethod: 'ceil' }),
);

const endTime = computed(() =>
  roundToNearestHours(props.endOn, { roundingMethod: 'floor' }),
);

const times = computed(() => {
  if (props.step) {
    return eachMinuteOfInterval(
      { start: startTime.value, end: endTime.value },
      { step: props.step },
    );
  }
  return eachHourOfInterval({ start: startTime.value, end: endTime.value });
});

const timeItemFactory = (time: Date): Item<Date> => {
  const title = props.itemTitleFormatFunction
    ? props.itemTitleFormatFunction(time)
    : getFormattedDate(time.getTime());
  return {
    key: time.getTime().toString(),
    title,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    icon: props.hideIcon ? undefined : 'clock',
    iconClasses: 'd-w12 d-h12',
    titleClasses: 'd-fs-100',
    comparableValue: time.getTime(),
    ref: time,
  } as Item<Date>;
};

const groups = computed(() => {
  return [
    {
      items: times.value.map(timeItemFactory).filter((time) => {
        return !!time.title?.toLowerCase().includes(filter.value.toLowerCase());
      }),
    },
  ];
});

const timeZoneDetail = computed(() =>
  props.showTimezone ? getFormattedTimezoneDetail() : '',
);

watch(
  [model],
  () => {
    if (model.value === modelValue.value?.ref) return;
    log.debug('Model changed', model.value);
    modelValue.value = model.value ? timeItemFactory(model.value) : null;
  },
  { immediate: true },
);

watch([modelValue], () => {
  if (model.value === modelValue.value?.ref) return;
  log.debug('ModelValue changed, updating model', modelValue.value);
  model.value = modelValue.value?.ref ?? null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="modelValue"
      :disabled="disabled"
      :label="label ?? $t('TIME_SELECT_LABEL')"
      :description="timeZoneDetail"
      :debounce="0"
      :placeholder="placeholder ?? $t('TIME_SELECT_PLACEHOLDER')"
      :messages="$ta('TIME_SELECT_MESSAGES')"
      :list-aria-label="$t('TIME_SELECT_ARIA_LABEL')"
      :groups="groups"
      :required="required"
      :min-value="minTime?.getTime()"
      :max-value="maxTime?.getTime()"
      :error-messages="errorMessages"
      right-icon="chevrons-up-down"
      :show-list-icons="false"
      list-class="d-hmx216"
      size="sm"
      @update:search="(value) => (filter = value)"
      @update:opened="(value) => emit('update:opened', value)"
      @update:error="(value) => emit('update:error', value)"
    />
  </div>
</template>
